import { X } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect } from "react";

/**
 * Right-sidebar-first pattern: detail views and editors open in a panel
 * anchored to the right edge rather than navigating away from the list.
 */
export function RightSidebarPanel({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  wide,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        className="shell-panel-region fixed inset-x-0 z-[80] bg-black/50 lg:bg-black/30"
        onClick={onClose}
      />
      <aside
        className={`panel-in shell-panel-region fixed right-0 z-[85] flex w-full flex-col border-l border-line bg-surface-2 shadow-2xl ${
          wide ? "max-w-2xl" : "max-w-md"
        }`}
      >
        <header className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 className="font-display truncate text-sm font-semibold">{title}</h2>
            {subtitle && <p className="mt-0.5 truncate text-xs text-muted">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-input-bg text-muted transition-colors hover:bg-hover hover:text-foreground"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <footer className="border-t border-line bg-hover px-5 py-3">{footer}</footer>}
      </aside>
    </>
  );
}
